import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type {
  CreateStandingsConfiguration,
  UpdateStandingsConfiguration,
} from "~pages/StandingsConfigurations/types/upsertStandingsConfiguration.ts";
import { useCreateStandingsConfiguration } from "./useCreateStandingsConfiguration.ts";
import { useDeleteStandingsConfiguration } from "./useDeleteStandingsConfiguration.ts";
import { useUpdateStandingsConfiguration } from "./useUpdateStandingsConfiguration.ts";

export const useStandingsConfigurationService = () => {
  const { mutateAsync: createStandingsConfigurationFn, isPending: isCreatePending } = useCreateStandingsConfiguration();
  const { mutateAsync: updateStandingsConfigurationFn, isPending: isUpdatePending } = useUpdateStandingsConfiguration();
  const { mutateAsync: deleteStandingsConfigurationFn, isPending: isDeletePending } = useDeleteStandingsConfiguration();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove } = useApiOperations(ApiOperationItems.STANDINGS_CONFIGURATION);

  const createStandingsConfiguration = async (payload: CreateStandingsConfiguration) => {
    const result = await create(() => createStandingsConfigurationFn(payload), {
      successMessageId: "standings.configuration.add.success",
      isSucceeded: () => true,
    });

    if (result !== undefined) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/standings-configurations`, {
        state: { search: payload.nameLocal },
      });
    }

    return result;
  };

  const updateStandingsConfiguration = (
    standingConfigurationId: string,
    payload: Omit<UpdateStandingsConfiguration, "standingConfigurationId">,
  ) =>
    update(() => updateStandingsConfigurationFn({ ...payload, standingConfigurationId }), {
      successMessageId: "standings.configuration.update.success",
    });

  const deleteStandingsConfiguration = async (standingConfigurationId: string) =>
    Boolean(
      await remove(() => deleteStandingsConfigurationFn(standingConfigurationId), {
        successMessageId: "standings.configuration.delete.success",
      }),
    );

  return {
    createStandingsConfiguration,
    isCreatePending,
    updateStandingsConfiguration,
    isUpdatePending,
    deleteStandingsConfiguration,
    isDeletePending,
  };
};
